import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { useSessionSearch } from '@/components/layout/Navigation/sessions/hooks/useSessionSearch';
import {
  isEHRAppointment,
  useRetrieveAppointmentOrSchedule,
} from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import { isEhrAppointmentLinked } from '@/features/consolidatedSessionsList/utils/isEhrAppointmentLinked';
import { type UpcomingSession } from '@/features/uploadSessionSchedule/types';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { getSessionListSummary } from '@heidi/session/contracts';
import { safeToISOString } from '@heidi/utils/dates';
import { isAfter, isSameDay, isValid } from 'date-fns';
import { useAtomValue } from 'jotai';
import { orderBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';

export const useUpcomingSessionsList = () => {
  const scheduleDate = useAtomValue(scheduleDateAtom);
  const {
    sessionSearchResult,
    sessionsHiddenByQueuedDelete,
    isPendingSessions,
    refetchSessions,
  } = useSessionSearch();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const {
    searchResult: ehrSearchResult,
    isPending: isPendingRetrieveAppointmentOrSchedule,
    refetch: refetchAppointmentOrSchedule,
  } = useRetrieveAppointmentOrSchedule();

  const heidiSessions = useMemo(() => {
    return (
      sessionSearchResult
        ?.map((session) => {
          const parsedDate = parseTimeStamp(
            session.created_time_local ?? '',
            !session?.ehr_appt_id
          );
          const isoString = safeToISOString(parsedDate);
          if (!isoString) {
            return null;
          }
          return {
            isFromEhr: !!session?.ehr_appt_id,
            session_id: session.session_id,
            kinde_gp_id: session.kinde_gp_id,
            created_time_local: isoString,
            displayName:
              session.patient_profile?.demographic_string ||
              session.patient.demographic_details ||
              // Prefer the editable gist over the generation-time name.
              getSessionListSummary(session) ||
              '',
            patient_profile: session.patient_profile,
            ehr_appt_id: session.ehr_appt_id,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) || []
    );
  }, [sessionSearchResult]);

  // Same carve-out as the consolidated list: the LINK check must see the
  // sessions the queued-delete read filter hid, or an appointment linked to a
  // session deleted offline resurfaces here carrying that session's id — and
  // this tab's click handler has no `!session.session_id` guard at all, so it
  // takes the link/create branch against a session that is already gone.
  const linkCheckSessions = useMemo(
    () => [...heidiSessions, ...sessionsHiddenByQueuedDelete],
    [heidiSessions, sessionsHiddenByQueuedDelete]
  );

  const ehrAppointments = useMemo(() => {
    if (!ehrSearchResult || isReviewingOtherUser) {
      return [];
    }
    return ehrSearchResult
      .filter((appointment) => !isEhrAppointmentLinked(appointment, linkCheckSessions))
      .map((appointment) => {
        if (isEHRAppointment(appointment)) {
          const utcAppointmentDate = parseTimeStamp(
            appointment.appointment_date_time ?? '',
            false
          );
          const isoString = safeToISOString(utcAppointmentDate);
          if (!isoString) {
            return null;
          }
          return {
            isFromEhr: true,
            session_id: appointment.session_id,
            kinde_gp_id: appointment.user_id,
            created_time_local: isoString,
            displayName:
              `${appointment.patient_first_name || ''} ${appointment.patient_last_name || ''}`.trim(),
            ehrApptData: appointment,
          };
        }

        const utcAppointmentDate = parseTimeStamp(
          appointment.appointment.start ?? '',
          false
        );
        const isoString = safeToISOString(utcAppointmentDate);
        if (!isoString) {
          return null;
        }
        return {
          isFromEhr: true,
          session_id: appointment.appointment.session?.session_id,
          kinde_gp_id: null,
          created_time_local: isoString,
          displayName:
            `${appointment.patient?.name?.[0]?.given?.join(' ') || ''} ${appointment.patient?.name?.[0]?.family || ''}`.trim(),
          ehrApptData: appointment,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [ehrSearchResult, linkCheckSessions, isReviewingOtherUser]);

  const upcomingSessions: UpcomingSession[] = useMemo(() => {
    // Combine both lists
    const combinedList = [...heidiSessions, ...ehrAppointments];

    let filteredSessions = combinedList;
    if (scheduleDate) {
      const today = new Date();
      filteredSessions = filteredSessions?.filter((session) => {
        if (!session.created_time_local) {
          return false;
        }
        const sessionDate = parseTimeStamp(session.created_time_local);
        if (!isValid(sessionDate)) {
          return false;
        }
        if (
          !!session.session_id &&
          isSameDay(sessionDate, today) &&
          isSameDay(sessionDate, scheduleDate)
        ) {
          return isAfter(sessionDate, today);
        }
        return isSameDay(sessionDate, scheduleDate);
      });
    } else {
      filteredSessions = filteredSessions?.filter((session) => {
        const sessionDate = parseTimeStamp(session.created_time_local);
        return isAfter(sessionDate, new Date());
      });
    }
    return orderBy(filteredSessions, ['created_time_local'], ['asc']);
  }, [heidiSessions, ehrAppointments, scheduleDate]);

  const refetchUpcomingSessions = useCallback(() => {
    if (integrationEhrProvider && !isReviewingOtherUser) {
      void refetchAppointmentOrSchedule();
    }
    void refetchSessions();
  }, [
    integrationEhrProvider,
    isReviewingOtherUser,
    refetchSessions,
    refetchAppointmentOrSchedule,
  ]);

  const isPending = useMemo(() => {
    return (
      (integrationEhrProvider &&
        !isReviewingOtherUser &&
        isPendingRetrieveAppointmentOrSchedule) ||
      isPendingSessions
    );
  }, [
    integrationEhrProvider,
    isReviewingOtherUser,
    isPendingRetrieveAppointmentOrSchedule,
    isPendingSessions,
  ]);

  return {
    upcomingSessions,
    ehrSearchResult,
    refetchUpcomingSessions,
    isPending,
  };
};
